/**
 * NotFoundPage - Full-viewport 404 page
 *
 * Purpose: Friendly but clear 404 handler
 * Layout: Broken hero + 404 number + friendly message + CTA
 * Animation: Staggered fade-in (404 → title → subtitle → CTA)
 */

import { useState, useEffect } from 'react';
import { HeroImage } from './HeroImage';
import { BRAND, BRAND_IMAGES } from '@/styles/tokens';

interface NotFoundPageProps {
  onGoHome?: () => void;
}

const useFadeIn = (delay = 0) => {
  const [mounted, setMounted] = useState(false);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    setTimeout(() => setMounted(true), 100 + delay);
  }, [delay]);

  return {
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(15px)',
    transition: prefersReducedMotion
      ? 'none'
      : 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  };
};

export function NotFoundPage({ onGoHome }: NotFoundPageProps) {
  const numberStyle = useFadeIn(350);
  const titleStyle = useFadeIn(450);
  const subtitleStyle = useFadeIn(550);
  const ctaStyle = useFadeIn(650);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: BRAND.cream,
        padding: '40px 24px',
      }}
    >
      {/* Broken hero */}
      <div style={{ ...numberStyle, marginBottom: '32px' }}>
        <HeroImage
          src={BRAND_IMAGES.hero}
          height={190}
          broken
          fadeColor={BRAND.cream}
          style={{
            maxWidth: '400px',
            width: '100%',
            borderRadius: '12px',
          }}
        />
      </div>

      {/* 404 number */}
      <div
        style={{
          ...numberStyle,
          fontFamily: BRAND.fontSerif,
          fontSize: '60px',
          fontWeight: 'bold',
          lineHeight: 1,
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ color: BRAND.ink }}>4</span>
        <span style={{ color: BRAND.coral }}>0</span>
        <span style={{ color: BRAND.ink }}>4</span>
      </div>

      {/* Title */}
      <h1
        style={{
          ...titleStyle,
          fontFamily: BRAND.fontSerif,
          fontSize: '19px',
          fontWeight: 'bold',
          color: BRAND.ink,
          textAlign: 'center',
          marginBottom: '8px',
        }}
      >
        This page wandered off the canvas.
      </h1>

      {/* Subtitle */}
      <p
        style={{
          ...subtitleStyle,
          fontFamily: BRAND.fontSans,
          fontSize: '14px',
          lineHeight: 1.5,
          color: BRAND.inkLight,
          textAlign: 'center',
          maxWidth: '280px',
          marginBottom: '32px',
        }}
      >
        The page you're looking for doesn't exist or has been moved.
      </p>

      {/* CTA button */}
      <button
        onClick={onGoHome}
        style={{
          ...ctaStyle,
          fontFamily: BRAND.fontSans,
          fontSize: '14px',
          fontWeight: 600,
          color: BRAND.white,
          background: `linear-gradient(135deg, ${BRAND.teal}, ${BRAND.tealDark})`,
          padding: '14px 28px',
          borderRadius: '12px',
          border: 'none',
          cursor: 'pointer',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(61, 171, 165, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        Back to Dashboard
      </button>
    </div>
  );
}

export default NotFoundPage;
